import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../../lib/api'
import { quotaGateFromReason, type QuotaGateState } from '../../lib/quota'
import QuotaGate from '../QuotaGate'
import ResolveReview, { type ApplyPayload, type ReviewData } from './ResolveReview'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ExistingProof {
  id: string
  employer: string
  role_title: string
  period: string | null
  summary: string
}

interface AccomplishmentClarification {
  kind?: 'accomplishment'
  question_index: number
  question_total: number
  question_id: string
  question_text: string
  source_text: string
  existing_proof: ExistingProof
}

interface ProfileClarification {
  kind: 'profile'
  question_index: number
  question_total: number
  question_id: string
  question_text: string
  field: string
  current_value: string
  extracted_value: string
  source_label: string
}

type ClarificationData = AccomplishmentClarification | ProfileClarification


interface PollResponse {
  status: 'analysing' | 'clarification' | 'review' | 'error'
  progress_step?: string
  clarification?: ClarificationData
  review?: ReviewData
  error_message?: string
  // Present only for LLM-blocked failures (invalid key, hit cap, billing).
  error_reason?: string | null
  details?: { spent: number | null; cap: number | null; source: string } | null
}


type Stage = 'upload' | 'analysing' | 'clarification' | 'review' | 'complete'

interface UploadCVModalProps {
  onClose: () => void
  onComplete: (newCount: number, updatedCount: number) => void
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function UploadCVModal({ onClose, onComplete }: UploadCVModalProps) {
  const [stage, setStage] = useState<Stage>('upload')
  const [files, setFiles] = useState<File[]>([])
  const [fileError, setFileError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [progressLabel, setProgressLabel] = useState('Extracting content from your CV...')
  const [pollError, setPollError] = useState<string | null>(null)
  // Set when analysis is blocked by an invalid key / hit cap; renders the gate.
  const [quotaGate, setQuotaGate] = useState<QuotaGateState | null>(null)
  const [clarification, setClarification] = useState<ClarificationData | null>(null)
  const [review, setReview] = useState<ReviewData | null>(null)
  const [applying, setApplying] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [submittingClarify, setSubmittingClarify] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pollStartRef = useRef<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [showCancelBtn, setShowCancelBtn] = useState(false)
  const [progressStep, setProgressStep] = useState<string>('extracting')

  const STEP_LABELS: Record<string, string> = {
    extracting: 'Extracting accomplishments from your CVs...',
    comparing: 'Comparing against your library...',
    classifying: 'Identifying new and updated proofs...',
  }
  const STEP_WIDTHS: Record<string, string> = {
    extracting: '30%',
    comparing: '65%',
    classifying: '88%',
  }

  const ALLOWED_EXTENSIONS = ['.docx', '.pdf', '.txt']
  const MAX_FILES = 5
  const MAX_SIZE_MB = 10

  // Lock background scroll while the modal is open, so a wheel/touch gesture over
  // the backdrop scrolls nothing instead of the page behind the dialog.
  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [])

  // ---------------------------------------------------------------------------
  // Polling
  // ---------------------------------------------------------------------------

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])

  useEffect(() => {
    if (stage !== 'analysing' || !sessionId) return
    pollStartRef.current = Date.now()
    setShowCancelBtn(false)
    setProgressStep('extracting')
    setProgressLabel(STEP_LABELS['extracting'])

    // Show cancel option only after 2 min — extraction legitimately takes
    // 60–120 s for large CVs, so surfacing a cancel earlier creates false alarm.
    const cancelTimer = setTimeout(() => setShowCancelBtn(true), 2 * 60 * 1000)

    pollRef.current = setInterval(async () => {
      // Hard timeout at 8 min — gives room for ~79s LLM extraction + model warmup + batch embedding
      if (pollStartRef.current && Date.now() - pollStartRef.current > 8 * 60 * 1000) {
        stopPolling()
        clearTimeout(cancelTimer)
        setPollError('Analysis is taking longer than expected. Please try uploading again.')
        return
      }

      try {
        const res = await api.get<{ data: PollResponse }>(`/api/v1/library/upload/${sessionId}`)
        const data = res.data
        if (data.status === 'clarification' && data.clarification) {
          stopPolling()
          clearTimeout(cancelTimer)
          setClarification(data.clarification)
          setStage('clarification')
        } else if (data.status === 'review' && data.review) {
          stopPolling()
          clearTimeout(cancelTimer)
          setReview(data.review)
          setStage('review')
        } else if (data.status === 'error') {
          stopPolling()
          clearTimeout(cancelTimer)
          // An invalid key / hit cap / billing block is actionable — show the
          // gate (fix key in place, then re-run) instead of a dead-end message.
          const gate = quotaGateFromReason(data)
          if (gate) {
            setUploading(false)
            setQuotaGate(gate)
          } else {
            setPollError(data.error_message ?? 'Analysis failed.')
          }
        } else if (data.status === 'analysing' && data.progress_step) {
          // Drive label and progress bar from actual backend step
          setProgressStep(data.progress_step)
          setProgressLabel(STEP_LABELS[data.progress_step] ?? progressLabel)
        }
      } catch {
        // Transient errors during polling — don't stop, keep trying
      }
    }, 2000)

    return () => {
      stopPolling()
      clearTimeout(cancelTimer)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, sessionId, stopPolling])

  // ---------------------------------------------------------------------------
  // File handling
  // ---------------------------------------------------------------------------

  const validateAndAddFiles = (incoming: FileList | File[]) => {
    setFileError(null)
    const list = Array.from(incoming)
    const next = [...files]
    for (const file of list) {
      const ext = '.' + file.name.split('.').pop()?.toLowerCase()
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        setFileError(`"${file.name}" is not a supported format. Use .docx, .pdf, or .txt.`)
        return
      }
      if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        setFileError(`"${file.name}" exceeds the 10 MB size limit.`)
        return
      }
      if (next.length >= MAX_FILES) {
        setFileError(`Maximum ${MAX_FILES} files per upload.`)
        return
      }
      next.push(file)
    }
    setFiles(next)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    if (uploading) return  // no file changes once the upload request is in flight
    validateAndAddFiles(e.dataTransfer.files)
  }

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) validateAndAddFiles(e.target.files)
  }

  const removeFile = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx))
  }

  // ---------------------------------------------------------------------------
  // Upload & start analysis
  // ---------------------------------------------------------------------------

  // Upload the currently-held files and (re)start analysis. Files stay in React
  // state throughout, so resuming after an invalid-key fix re-uploads the SAME
  // files — the user re-selects nothing.
  const startAnalysis = async () => {
    setUploading(true)
    setPollError(null)
    setQuotaGate(null)
    try {
      const res = await api.upload<{ data: { session_id: string } }>(
        '/api/v1/library/upload',
        files
      )
      setSessionId(res.data.session_id)
      setProgressLabel('Extracting content from your CV...')
      setStage('analysing')
    } catch (err) {
      setFileError((err as Error).message ?? 'Upload failed. Please try again.')
      setUploading(false)
    }
  }

  const handleUpload = async () => {
    if (files.length === 0 || uploading) return
    await startAnalysis()
  }

  // ---------------------------------------------------------------------------
  // Clarification
  // ---------------------------------------------------------------------------

  const handleClarifyAnswer = async (
    answer: 'same' | 'different' | 'skip' | 'keep' | 'replace',
  ) => {
    if (!sessionId || !clarification || submittingClarify) return  // guard double-submit
    setSubmittingClarify(true)
    try {
      const res = await api.post<{ data: PollResponse }>(
        `/api/v1/library/upload/${sessionId}/clarify`,
        { question_id: clarification.question_id, answer }
      )
      const data = res.data
      if (data.status === 'clarification' && data.clarification) {
        setClarification(data.clarification)
      } else if (data.status === 'review' && data.review) {
        setReview(data.review)
        setClarification(null)
        setStage('review')
      }
    } catch (err) {
      setPollError((err as Error).message)
    } finally {
      setSubmittingClarify(false)
    }
  }


  // ---------------------------------------------------------------------------
  // Apply
  // ---------------------------------------------------------------------------

  const handleApply = async (payload: ApplyPayload) => {
    if (!sessionId || applying) return  // re-entry guard (belt-and-suspenders with the disabled button)
    setApplying(true)
    try {
      const res = await api.post<{ data: { new_count: number; updated_count: number; framing_count: number } }>(
        `/api/v1/library/upload/${sessionId}/apply`,
        payload,
      )
      onComplete(res.data.new_count, res.data.updated_count + res.data.framing_count)
    } catch (err) {
      setPollError((err as Error).message)
      setApplying(false)
    }
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-2xl rounded-xl bg-white shadow-2xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Stage: Upload */}
        {stage === 'upload' && (
          <>
            <div className="border-b border-stone-100 px-6 py-5 flex items-center justify-between">
              <h2 className="font-semibold text-stone-800">Upload a CV</h2>
              <button onClick={onClose} className="text-stone-400 hover:text-stone-600 text-xl leading-none">&times;</button>
            </div>
            <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
              <p className="text-sm text-stone-500">
                Proof of Work will scan the uploaded file for proofs not yet in your library.
                Existing proofs will not be removed or overwritten without your approval.
              </p>
              <div
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                className={`rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors ${
                  isDragging ? 'border-stone-400 bg-stone-50' : 'border-stone-200'
                }`}
              >
                <p className="text-sm text-stone-500 mb-2">
                  Drag and drop or{' '}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="text-stone-800 underline hover:no-underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
                  >
                    choose files
                  </button>
                </p>
                <p className="text-xs text-stone-400">
                  .docx &middot; .pdf &middot; .txt &nbsp;&nbsp; Max {MAX_SIZE_MB} MB per file &middot; Up to {MAX_FILES} files
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".docx,.pdf,.txt"
                  className="hidden"
                  disabled={uploading}
                  onChange={handleFileInput}
                />
              </div>
              {files.length > 0 && (
                <ul className="space-y-1">
                  {files.map((f, i) => (
                    <li key={i} className="flex items-center justify-between text-sm text-stone-700 rounded bg-stone-50 px-3 py-2">
                      <span className="truncate">{f.name}</span>
                      <button
                        onClick={() => removeFile(i)}
                        disabled={uploading}
                        className="ml-3 text-stone-400 hover:text-red-500 shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        &times;
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {fileError && (
                <p className="text-xs text-red-600">{fileError}</p>
              )}
            </div>
            <div className="border-t border-stone-100 px-6 py-4 flex justify-end gap-3">
              <button onClick={onClose} className="rounded px-4 py-2 text-sm text-stone-500 hover:bg-stone-50">
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={files.length === 0 || uploading}
                className="rounded px-4 py-2 text-sm text-white bg-stone-800 hover:bg-stone-700 disabled:opacity-40"
              >
                {uploading ? 'Uploading…' : 'Upload and Analyse →'}
              </button>
            </div>
          </>
        )}

        {/* Stage: Analysing */}
        {stage === 'analysing' && (
          <div className="px-6 py-10 flex-1 flex flex-col items-center justify-center space-y-5">
            {quotaGate ? (
              <QuotaGate
                gate={quotaGate}
                variant="card"
                onKeyResolved={quotaGate.kind === 'byo_key_invalid' ? startAnalysis : undefined}
              />
            ) : pollError ? (
              <>
                <p className="text-sm text-red-600">{pollError}</p>
                <button
                  onClick={onClose}
                  className="rounded px-4 py-2 text-sm text-white bg-stone-800 hover:bg-stone-700"
                >
                  Something went wrong — start over
                </button>
              </>
            ) : (
              <>
                <h2 className="font-semibold text-stone-800">Analysing your CV</h2>
                <div className="w-full max-w-xs bg-stone-100 rounded-full h-1.5">
                  <div
                    className="bg-stone-800 h-1.5 rounded-full transition-all duration-700"
                    style={{ width: STEP_WIDTHS[progressStep] ?? '30%' }}
                  />
                </div>
                <p className="text-sm text-stone-500">{progressLabel}</p>
                <p className="text-xs text-stone-400">This usually takes 1–2 minutes.</p>
                {showCancelBtn && (
                  <button
                    onClick={() => { stopPolling(); onClose() }}
                    className="mt-2 text-xs text-stone-400 hover:text-stone-600 underline"
                  >
                    Taking too long? Cancel and try again
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* Stage: Clarification */}
        {stage === 'clarification' && clarification && clarification.kind !== 'profile' && (
          <>
            <div className="border-b border-stone-100 px-6 py-5 flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-stone-800">A couple of questions before we continue</h2>
                <p className="text-xs text-stone-400 mt-0.5">
                  Question {clarification.question_index} of {clarification.question_total}
                </p>
              </div>
            </div>
            <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
              <p className="text-sm text-stone-600">We found something that might be related to an existing proof.</p>
              <div>
                <p className="text-xs font-medium text-stone-500 mb-1.5">From your uploaded CV</p>
                <div className="rounded bg-stone-50 border border-stone-200 px-4 py-3 text-sm text-stone-700 italic">
                  "{clarification.source_text}"
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-stone-500 mb-1.5">Existing proof in your library</p>
                <div className="rounded bg-stone-50 border border-stone-200 px-4 py-3">
                  <p className="text-xs font-medium text-stone-700">
                    {clarification.existing_proof.employer} &middot; {clarification.existing_proof.role_title}
                    {clarification.existing_proof.period && ` · ${clarification.existing_proof.period}`}
                  </p>
                  <p className="text-sm text-stone-500 mt-1">{clarification.existing_proof.summary}</p>
                </div>
              </div>
              <p className="text-sm text-stone-700">{clarification.question_text}</p>
            </div>
            <div className="border-t border-stone-100 px-6 py-4 space-y-2">
              {(
                [
                  { answer: 'same' as const, label: 'Same accomplishment — add the detail to the existing proof' },
                  { answer: 'different' as const, label: 'Different accomplishment — create a new proof' },
                  { answer: 'skip' as const, label: 'Skip for now' },
                ] as const
              ).map(({ answer, label }) => (
                <button
                  key={answer}
                  onClick={() => handleClarifyAnswer(answer)}
                  disabled={submittingClarify}
                  className="w-full text-left rounded border border-stone-200 px-4 py-2.5 text-sm text-stone-700 hover:bg-stone-50 hover:border-stone-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {label}
                </button>
              ))}
            </div>
          </>
        )}

        {/* Stage: Clarification (profile_update — Phase 3) */}
        {stage === 'clarification' && clarification && clarification.kind === 'profile' && (
          <>
            <div className="border-b border-stone-100 px-6 py-5 flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-stone-800">Profile update</h2>
                <p className="text-xs text-stone-400 mt-0.5">
                  Question {clarification.question_index} of {clarification.question_total}
                </p>
              </div>
            </div>
            <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
              <p className="text-sm text-stone-700">{clarification.question_text}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="rounded border border-stone-200 px-4 py-3">
                  <p className="text-xs font-medium text-stone-500 mb-1">Current — kept on file</p>
                  <p className="text-sm text-stone-700 break-words">{clarification.current_value || '—'}</p>
                </div>
                <div className="rounded border border-stone-200 px-4 py-3">
                  <p className="text-xs font-medium text-stone-500 mb-1">
                    New — from {clarification.source_label || 'your latest upload'}
                  </p>
                  <p className="text-sm text-stone-700 break-words">{clarification.extracted_value || '—'}</p>
                </div>
              </div>
            </div>
            <div className="border-t border-stone-100 px-6 py-4 space-y-2">
              {(
                [
                  { answer: 'keep' as const, label: 'Keep current' },
                  { answer: 'replace' as const, label: 'Use new' },
                  { answer: 'skip' as const, label: 'Skip for now' },
                ] as const
              ).map(({ answer, label }) => (
                <button
                  key={answer}
                  onClick={() => handleClarifyAnswer(answer)}
                  disabled={submittingClarify}
                  className="w-full text-left rounded border border-stone-200 px-4 py-2.5 text-sm text-stone-700 hover:bg-stone-50 hover:border-stone-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {label}
                </button>
              ))}
            </div>
          </>
        )}

        {/* Stage: Review — the shared resolve screen */}
        {stage === 'review' && review && (
          <ResolveReview
            review={review}
            applying={applying}
            error={pollError}
            incomingLabel="From this upload"
            title="Review your upload"
            subtitle="Nothing is added to your Library until you confirm."
            applyLabel="Add to Library"
            discardLabel="Discard this upload"
            onApply={handleApply}
            onDiscard={onClose}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  )
}

